import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { CompactOffer } from 'components/Offer/Compact';
import { OfferTrackingContextProvider } from 'context/offerTracking';
import { OfferViewContext, OfferViewType } from 'context/offerViewContext';
import { useOfferContainer } from 'hooks/useOfferContainer';
import { getClickedOfferId } from 'modules/accommodation/utils';
import { getAnchorPriceNightlyPerRoom, getSplitBookingDetails } from 'modules/sapiSearch/selectors';
import { RootState } from 'store';
import { Card } from '@daedalus/atlas/Card';
import { Box } from '@daedalus/atlas/helpers/Box';
import { useCachedSearchTopOffers } from '../useCachedSearchTopOffers';
import { useTrackDisplayed, useTrackDisplayedProps } from './useTrackDisplayed';
const SafeAreaWrapper = /*#__PURE__*/styled('div')({
  name: "SafeAreaWrapper",
  class: "s1d3wtw",
  propsAsIs: false
});
interface BottomCTABaseCardLayoutProps {
  children: React.ReactNode;
  noBorderStyles?: boolean;
}
const BottomCTABaseCardLayout: React.FC<BottomCTABaseCardLayoutProps> = ({
  children,
  noBorderStyles
}) => {
  return noBorderStyles ? <SafeAreaWrapper>{children}</SafeAreaWrapper> : <Card backgroundColorPath="background.neutral.c000" borderRadius="lg" hasRoundedBottom={false} shadow="fixedReverse">
      <SafeAreaWrapper>{children}</SafeAreaWrapper>
    </Card>;
};
const BottomCTATrackDisplayedWrapper: React.FC<useTrackDisplayedProps & {
  noBorderStyles?: boolean;
}> = ({
  children,
  noBorderStyles,
  ...props
}) => {
  useTrackDisplayed(props);
  return <BottomCTABaseCardLayout noBorderStyles={noBorderStyles}>
      {children}
    </BottomCTABaseCardLayout>;
};
interface BottomCTAProps {
  hotelId: string;
  sourceComponent: string;
  reportHeight?: (height: number) => void;
  noBorderStyles?: boolean;
}
export const BottomCTA: React.FC<BottomCTAProps> = ({
  hotelId,
  sourceComponent,
  noBorderStyles,
  reportHeight
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const selectedVioOfferId = getClickedOfferId();
  const {
    offerEntity,
    isComplete,
    searchedParameters
  } = useCachedSearchTopOffers({
    hotelId
  });
  const anchorPrice = useSelector((state: RootState) => getAnchorPriceNightlyPerRoom(state, offerEntity));
  const {
    anchorPriceTotal,
    offerVariant,
    primaryOffer,
    primaryOfferIndex
  } = useOfferContainer({
    offerEntity,
    offerId: selectedVioOfferId,
    hotelId
  });
  const {
    showSplitBookingLoader,
    splitBookingOffers,
    hasSplitBookingOffer,
    splitBookingPosition,
    splitBookingType
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  useLayoutEffect(() => reportHeight?.(ref.current?.clientHeight || 0), [offerEntity, isComplete, reportHeight]);
  const isSplitBooking = hasSplitBookingOffer && splitBookingPosition == 0;
  const isLoading = !isComplete || showSplitBookingLoader;
  if (isComplete && !primaryOffer && !isSplitBooking) return <div ref={ref} />;
  const renderCompactOffer = () => {
    if (isLoading) return <CompactOffer variant="CompactDefault" isLoading={isLoading} />;
    if (isSplitBooking) return <CompactOffer isSplitBooking splitBookingType={splitBookingType} splitBookingOffers={splitBookingOffers} variant="CompactDefault" hotelId={hotelId} sourceComponent={sourceComponent} hasTag={false} />;
    return <div data-id="StaticOffer">
        <CompactOffer variant="CompactDefault" hotelId={hotelId} offer={primaryOffer} anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} offerVariant={offerVariant} hasTag={false} hasInteraction sourceComponent={sourceComponent} offerPosition={primaryOfferIndex} />
      </div>;
  };
  return <div ref={ref}>
      <OfferTrackingContextProvider offerEntity={offerEntity} parameters={searchedParameters}>
        <OfferViewContext.Provider value={OfferViewType.AP_FOOTER}>
          <BottomCTATrackDisplayedWrapper hotelId={hotelId} offer={primaryOffer} sourceComponent={sourceComponent} offerEntity={offerEntity} noBorderStyles={noBorderStyles}>
            <Box padding={noBorderStyles ? 's000' : 's400'}>
              {renderCompactOffer()}
            </Box>
          </BottomCTATrackDisplayedWrapper>
        </OfferViewContext.Provider>
      </OfferTrackingContextProvider>
    </div>;
};

require("./BottomCTA.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./BottomCTA.tsx");